import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles, Wand2, GitCompareArrows, Shuffle } from "lucide-react";
import BottleSilhouette from "../components/BottleSilhouette";
import DnaRadar from "../components/DnaRadar";
import StaffPickStrip from "../components/StaffPickStrip";
import DiscoveriesStrip from "../components/DiscoveriesStrip";
import { listGins } from "../lib/api";
import { CATEGORY_COLORS } from "../lib/constants";
import { api } from "../lib/api";
import { recordEvent } from "../lib/adminApi";

// Six rotating "gin personalities" for the hero radar
const HERO_PROFILES = [
  { name: "Classic London Dry", category: "classic",
    dna: { juniper: 5, citrus: 4, floral: 1, spice: 2, herbal: 2, fruit: 1, earthy: 1, coastal: 0, sweetness: 1, complexity: 2 } },
  { name: "Citrus Highball", category: "citrus",
    dna: { juniper: 3, citrus: 5, floral: 2, spice: 1, herbal: 2, fruit: 2, earthy: 0, coastal: 1, sweetness: 1, complexity: 3 } },
  { name: "Floral Garden", category: "floral",
    dna: { juniper: 3, citrus: 3, floral: 5, spice: 1, herbal: 3, fruit: 1, earthy: 0, coastal: 0, sweetness: 2, complexity: 4 } },
  { name: "Indian Spice", category: "spice",
    dna: { juniper: 4, citrus: 3, floral: 1, spice: 5, herbal: 2, fruit: 1, earthy: 3, coastal: 0, sweetness: 1, complexity: 5 } },
  { name: "Coastal Mineral", category: "coastal",
    dna: { juniper: 4, citrus: 2, floral: 1, spice: 1, herbal: 3, fruit: 0, earthy: 2, coastal: 5, sweetness: 0, complexity: 4 } },
  { name: "Wild Experimental", category: "experimental",
    dna: { juniper: 3, citrus: 2, floral: 1, spice: 3, herbal: 5, fruit: 1, earthy: 4, coastal: 0, sweetness: 0, complexity: 5 } },
];

const FLAVOR_CARDS = [
  { id: "Juniper-forward", label: "Classic Juniper", cat: "classic", line: "Pine, pepper, backbone." },
  { id: "Citrus", label: "Citrus", cat: "citrus", line: "Lemon, lime, sunshine." },
  { id: "Floral", label: "Floral", cat: "floral", line: "Rose, elderflower, jasmine." },
  { id: "Spice", label: "Spice", cat: "spice", line: "Cardamom, pepper, fire." },
  { id: "Coastal / Mineral", label: "Coastal", cat: "coastal", line: "Salt, kelp, breeze." },
  { id: "Herbal", label: "Experimental", cat: "experimental", line: "Hemp, coffee, broken bats." },
];

export default function Home() {
  const [heroIdx, setHeroIdx] = useState(0);
  const [indianGins, setIndianGins] = useState([]);
  const [featuredCount, setFeaturedCount] = useState(92);
  const [featuredFlights, setFeaturedFlights] = useState([]);

  useEffect(() => {
    const id = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_PROFILES.length), 3800);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    listGins({ country: "India", limit: 6 }).then((d) => setIndianGins(d.gins));
    listGins().then((d) => setFeaturedCount(d.count));
    api.get("/flights").then((r) => setFeaturedFlights(r.data.flights.slice(0, 4)));
    recordEvent("atlas_open");
  }, []);

  const heroProfile = HERO_PROFILES[heroIdx];

  return (
    <div className="overflow-hidden" data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center px-5 md:px-12 pt-10 pb-20">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[700px] h-[700px] opacity-50 slow-rotate">
            <div className="w-full h-full"
                 style={{ background: "conic-gradient(from 0deg, rgba(193,127,89,0.08), rgba(26,54,40,0.18), rgba(212,175,55,0.05), rgba(193,127,89,0.08))" }} />
          </div>
        </div>
        <div className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 space-y-7 relative z-10">
            <p className="overline text-[#C17F59] flex items-center gap-3" data-testid="hero-overline">
              <span className="font-hand normal-case tracking-normal text-base text-[#D89A74]">est. BENO</span>
              <span className="h-px w-12 bg-[#C17F59]/40" />
              India's Gin Archive
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              India's Largest <em className="not-italic text-[#C17F59]">Gin Menu</em>, Now <br className="hidden sm:block" />
              <span className="font-hand text-[#D89A74] text-6xl sm:text-7xl lg:text-8xl">Explorable.</span>
            </h1>
            <p className="text-base md:text-lg text-white/70 max-w-xl leading-relaxed">
              Explore BENO's gin collection by mood, flavor, country, botanical, style, price, and serve.
              {" "}A living atlas of <span className="text-white">{featuredCount}</span> gins, mapped by their BENO Gin DNA.
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Link to="/gin/match"
                    className="group inline-flex items-center gap-2 bg-[#C17F59] text-[#0E0F0C] px-7 py-4 text-xs uppercase tracking-[0.3em] hover:bg-[#D89A74] transition-colors"
                    data-testid="hero-find-my-gin">
                <Wand2 size={14} />
                Find My Gin
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/gin/explore"
                    className="inline-flex items-center gap-2 border border-white/20 px-6 py-4 text-xs uppercase tracking-[0.3em] hover:border-[#C17F59] hover:text-[#C17F59] transition-colors"
                    data-testid="hero-explore">
                <Sparkles size={14} />
                Explore the Atlas
              </Link>
              <Link to="/gin/surprise"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white/70 hover:text-[#C17F59] transition-colors px-2 py-4"
                    data-testid="hero-surprise">
                <Shuffle size={14} />
                Surprise Me
              </Link>
              <Link to="/gin/compare"
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white/70 hover:text-[#C17F59] transition-colors px-2 py-4"
                    data-testid="hero-compare">
                <GitCompareArrows size={14} />
                Compare
              </Link>
            </div>

            <div className="pt-8 grid grid-cols-3 gap-6 max-w-md">
              {[["92", "Gins"], ["10", "Countries"], ["10", "DNA Axes"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-4xl text-[#C17F59]">{n}</div>
                  <div className="overline text-white/50">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero DNA visualization */}
          <div className="lg:col-span-5 relative z-10">
            <div className="relative bg-[#15171A] border border-white/8 p-6 md:p-8">
              <div className="flex items-center justify-between mb-4">
                <span className="overline text-white/50">BENO Gin DNA</span>
                <span className="overline" style={{ color: CATEGORY_COLORS[heroProfile.category] }}>
                  Live Read
                </span>
              </div>
              <AnimatePresence mode="wait">
                <motion.h3
                  key={heroProfile.name}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4 }}
                  className="font-display text-3xl mb-1"
                >
                  {heroProfile.name}
                </motion.h3>
              </AnimatePresence>
              <p className="text-xs text-white/50 mb-4">An in-house flavor map to help you choose better.</p>
              <DnaRadar items={[{ name: heroProfile.name, dna: heroProfile.dna, category: heroProfile.category }]} height={300} />
              <div className="flex gap-1.5 mt-4 justify-center">
                {HERO_PROFILES.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setHeroIdx(i)}
                    className={`h-[2px] transition-all duration-500 ${i === heroIdx ? "w-8 bg-[#C17F59]" : "w-3 bg-white/20"}`}
                    aria-label={`profile ${i + 1}`}
                    data-testid={`hero-dot-${i}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Staff Pick — alive moment */}
      <StaffPickStrip />

      {/* Fresh Discoveries — living editorial layer */}
      <DiscoveriesStrip />

      {/* Pick a flavor */}
      <section className="px-5 md:px-12 py-20 max-w-7xl mx-auto" data-testid="flavor-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="overline text-[#C17F59] mb-2">02 · Browse by flavor</p>
            <h2 className="font-display text-4xl sm:text-5xl">Pick your mood.</h2>
          </div>
          <Link to="/gin/explore" className="hidden md:inline text-xs uppercase tracking-[0.3em] text-white/60 hover:text-[#C17F59] transition-colors">
            See all filters →
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {FLAVOR_CARDS.map((c, i) => (
            <Link key={c.id} to={`/explore?taste=${encodeURIComponent(c.id)}`}
                  className="group relative bg-[#15171A] border border-white/8 hover:border-[length:1px] hover:bg-[#1a1d20] p-5 md:p-6 aspect-square flex flex-col justify-between overflow-hidden transition-colors duration-500"
                  style={{ borderColor: undefined }}
                  data-testid={`flavor-card-${c.cat}`}>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 opacity-20 group-hover:opacity-50 transition-opacity duration-700"
                   style={{ background: `radial-gradient(circle, ${CATEGORY_COLORS[c.cat]} 0%, transparent 70%)` }} />
              <div className="relative">
                <div className="w-1 h-10 mb-3" style={{ background: CATEGORY_COLORS[c.cat] }} />
                <h3 className="font-display text-2xl leading-tight">{c.label}</h3>
              </div>
              <p className="relative text-xs text-white/60 group-hover:text-white/90 transition-colors">{c.line}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Flights */}
      <section className="px-5 md:px-12 py-20 max-w-7xl mx-auto" data-testid="featured-flights-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-10">
          <div className="md:col-span-7">
            <p className="overline text-[#C17F59] mb-2">04 · Tasting Flights</p>
            <h2 className="font-display text-4xl sm:text-5xl mb-3">Three small pours. One guided journey.</h2>
            <p className="text-white/65 max-w-2xl leading-relaxed">
              Twelve flights, curated by BENO's bartenders. Built around DNA, country, mood, education,
              and the collector's shelf. Pour, sip, take notes.
            </p>
            <p className="font-hand text-2xl text-[#D89A74] mt-4 -rotate-1">— a tasting, not a transaction.</p>
          </div>
          <div className="md:col-span-5 md:text-right">
            <Link to="/gin/flights" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#C17F59] hover:text-[#D89A74] transition-colors" data-testid="featured-flights-all">
              See all 12 flights
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {featuredFlights.map((f, i) => {
            const c = CATEGORY_COLORS[f.color_key] || CATEGORY_COLORS.classic;
            return (
              <Link key={f.id} to={`/gin/flights/${f.id}`}
                    className="group bg-[#15171A] border border-white/8 hover:border-white/25 p-5 transition-colors"
                    data-testid={`home-flight-${f.id}`}>
                <p className="font-hand text-base text-[#D89A74] mb-2 -rotate-1">~ {f.bartender}'s pour</p>
                <p className="overline mb-2" style={{ color: c }}>{f.kicker}</p>
                <h3 className="font-display text-2xl mb-3 leading-tight">{f.name}</h3>
                <div className="flex items-end justify-center gap-1 py-3 border-y border-white/5"
                     style={{ background: `radial-gradient(ellipse at center, ${c}15, transparent 70%)` }}>
                  {f.gin_previews.map((g, idx) => (
                    <BottleSilhouette key={g.id} category={g.category} size={36} />
                  ))}
                </div>
                <div className="flex items-center justify-between pt-4">
                  <p className="overline text-white/40">{f.pours}</p>
                  <p className="font-display text-xl">₹{f.price}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Indian Gin Revolution */}
      <section className="px-5 md:px-12 py-20 max-w-7xl mx-auto" data-testid="indian-revolution-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-10">
          <div className="md:col-span-7">
            <p className="overline text-[#C17F59] mb-2">03 · India</p>
            <h2 className="font-display text-4xl sm:text-5xl mb-3">The Indian Gin Revolution.</h2>
            <p className="text-white/65 max-w-2xl leading-relaxed">
              From Himalayan juniper to Goan kokum, Indian craft gin has rewritten its own rulebook in a decade.
              These are the bottles you'll tell your friends about.
            </p>
          </div>
          <div className="md:col-span-5 md:text-right">
            <Link to="/gin/explore?country=India" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#C17F59] hover:text-[#D89A74] transition-colors" data-testid="indian-see-all">
              See all 50+ Indian gins
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {indianGins.map((g, i) => (
            <Link key={g.id} to={`/gin/${g.id}`}
                  className="group flex flex-col items-center bg-[#15171A] border border-white/5 p-4 hover:border-[#C17F59]/40 transition-colors"
                  data-testid={`indian-gin-${g.id}`}>
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                <BottleSilhouette category={g.category} size={62} />
              </motion.div>
              <p className="font-display text-base mt-2 text-center leading-tight">{g.name}</p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">₹{g.price} / 30ml</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Surprise Me CTA */}
      <section className="px-5 md:px-12 py-24 max-w-7xl mx-auto" data-testid="surprise-cta-section">
        <div className="relative border border-[#C17F59]/30 p-10 md:p-16 overflow-hidden bg-[#15171A]/60 backdrop-blur-sm">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full"
               style={{ background: "radial-gradient(circle, rgba(193,127,89,0.18), transparent 70%)" }} />
          <div className="relative">
            <p className="overline text-[#C17F59] mb-3">04 · Surprise Me</p>
            <h2 className="font-display text-4xl sm:text-6xl leading-none mb-4">
              Don't choose. Let the Atlas choose for you.
            </h2>
            <p className="text-white/65 max-w-xl mb-7">
              A cinematic roulette across 92 gins. Choose a mood, watch the dial spin.
            </p>
            <Link to="/gin/surprise"
                  className="inline-flex items-center gap-3 bg-[#C17F59] text-[#0E0F0C] px-7 py-4 text-xs uppercase tracking-[0.3em] hover:bg-[#D89A74] transition-colors"
                  data-testid="surprise-cta-button">
              <Shuffle size={14} />
              Spin the Atlas
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
